import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/customerIdentityAdmin.css';

// Components
import Header from './components/Header';
import InfoBar from './components/InfoBar';
import Sidebar from './components/Sidebar';
import { NavUsageTracker } from './components/NavUsageTracker';
import { useVisibleNavItems } from './hooks/useVisibleNavItems';
import { SidebarContext } from './contexts/SidebarContext';

// Pages
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentsListV4 from './pages/v4/DocumentsListV4';
import DocumentsUploadV4 from './pages/v4/DocumentsUploadV4';
import DocumentsSearchV4 from './pages/v4/DocumentsSearchV4';
import DocumentsSettingsV4 from './pages/v4/DocumentsSettingsV4';
import DocumentsAssistantV4 from './pages/v4/DocumentsAssistantV4';
import DocumentsImportV4 from './pages/v4/DocumentsImportV4';
import DocumentsImportJobsV4 from './pages/v4/DocumentsImportJobsV4';
import DocumentsChatLogsV4 from './pages/v4/DocumentsChatLogsV4';
import DocumentsBatchesV4 from './pages/v4/DocumentsBatchesV4';
import Chat from './pages/Chat';
import Configurations from './pages/Configurations';
import AhriData from './pages/AhriData';
import AhriDashboard from './pages/AhriDashboard';
import AhriAssistant from './pages/AhriAssistant';
import AhriSolr from './pages/AhriSolr';
import OpenSearchConfig from './pages/OpenSearchConfig';
import AhriSystemShop from './pages/AhriSystemShop';
import AhriSystemShopV2 from './pages/AhriSystemShopV2';
import AhriSystemShopV3 from './pages/AhriSystemShopV3';
import AhriSystemShopEvents from './pages/AhriSystemShopEvents';
import AhriSystemCompare from './pages/AhriSystemCompare';
import AhriSystemsReview from './pages/AhriSystemsReview';
import AhriCustomerEligibility from './pages/AhriCustomerEligibility';
import AhriEligibilityRuleSets from './pages/AhriEligibilityRuleSets';
import RealtimeConnections from './pages/RealtimeConnections';
import SemantixSearch from './pages/SemantixSearch';
import OpenSearchEcommerce from './pages/OpenSearchEcommerce';
import AgenticShop from './pages/AgenticShop';
import Coupons from './pages/Coupons';
import Login from './pages/Login';
import Validate from './pages/Validate';
import LoggedOut from './pages/LoggedOut';
import AIImageMaskingSettings from './pages/aiImageMasking/Settings';
import TryBeforeYouBuy from './pages/aiImageMasking/TryBeforeYouBuy';
import TryBeforeYouBuyV2 from './pages/aiImageMasking/TryBeforeYouBuyV2';
import TryoutStudioNewUX from './pages/aiImageMasking/TryoutStudioNewUX';
import SwatchesV2 from './pages/aiImageMasking/SwatchesV2';
import UserGallery from './pages/aiImageMasking/UserGallery';
import Agents from './pages/aiImageMasking/Agents';
import ZipImport from './pages/aiImageMasking/ZipImport';
import ReportsDashboard from './pages/ReportsDashboard';
import ReportsDefine from './pages/ReportsDefine';
import ReportsSettings from './pages/ReportsSettings';
import CAD3D from './pages/CAD3D';
import DataQuality from './pages/DataQuality';
import OrderGenie from './pages/OrderX';
import OrderGenieV2 from './pages/OrderXV2';
import OrderGenieV3 from './pages/OrderXV3';
import OrderGenieV4 from './pages/OrderXV4';
import ContractorsHub from './pages/ContractorsHub';
import PriceSheets from './pages/PriceSheets';
import OrderGenieAccountAdmin from './pages/OrderXAccountAdmin';
import OrderGenieEmailInbox from './pages/OrderXEmailInbox';
import OrderGenieMailboxSetup from './pages/OrderXMailboxSetup';
import McpServerPage from './pages/McpServer';
import OrderGenieProductSearch from './pages/OrderXProductSearch';
import OrderGenieSettings from './pages/OrderXSettings';
import OrderGenieUsageDashboard from './pages/OrderXUsageDashboard';
import OrderGenieWorkflowSetup from './pages/OrderXWorkflowSetup';
import PurchaseProgram from './pages/PurchaseProgram';
import CiAdminApp from './pages/CiAdmin/CiAdminApp';
import CustomerIdentityEmbedded from './pages/CiAdmin/CustomerIdentityEmbedded';
import WebhookConsumer from './pages/WebhookConsumer';
import Styles from './pages/Styles';
import ConfigurableProducts from './pages/ConfigurableProducts';
import ConfigurableProductsList from './pages/ConfigurableProductsList';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

const Products = lazy(() => import('./pages/Products'));
const ProductsAiStandardizeJobs = lazy(() => import('./pages/ProductsAiStandardizeJobs'));
const ProductsAiSearchIndexing = lazy(() => import('./pages/ProductsAiSearchIndexing'));
const Inventory = lazy(() => import('./pages/Inventory'));
const ImportJobs = lazy(() => import('./pages/ImportJobs'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Redirects a retired /order-genie* path to its /order-x* replacement, preserving query/hash.
const LegacyOrderXRedirect = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { visibleItems } = useVisibleNavItems();
  const { reconnecting } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileSidebarOpen(false);
      } else {
        setIsSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleMobileSidebar = () => {
    if (!isMobile) {
      setIsSidebarCollapsed((prev) => !prev);
      return;
    }
    setMobileSidebarOpen((prev) => !prev);
  };

  const handleNavigate = () => {
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <NavUsageTracker items={visibleItems} />
      {reconnecting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#f59e0b',
            color: '#1c1917',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{ width: '14px', height: '14px', borderWidth: '2px' }}
          />
          App is trying to renew authorization&hellip;
        </div>
      )}
      <InfoBar />
      <Header onToggleSidebar={handleToggleMobileSidebar} isSidebarOpen={mobileSidebarOpen} />
      <div
        className="d-flex flex-grow-1 position-relative"
        style={{ minHeight: 0, minWidth: 0, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
      >
        <Sidebar
          collapsed={isSidebarCollapsed}
          onToggleCollapsed={(next) => setIsSidebarCollapsed(next)}
          mobileVisible={!isMobile || mobileSidebarOpen}
          onNavigate={handleNavigate}
        />
        {isMobile && mobileSidebarOpen && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
            style={{ zIndex: 900 }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
        <main
          className="flex-grow-1 p-2 p-md-3 p-lg-4"
          style={{ overflowY: 'auto', overflowX: 'hidden', minWidth: 0, width: 0, maxWidth: '100%' }}
          onClick={() => {
            if (isMobile && mobileSidebarOpen) {
              setMobileSidebarOpen(false);
            }
          }}
        >
          <SidebarContext.Provider value={{ collapsed: isSidebarCollapsed, setCollapsed: setIsSidebarCollapsed }}>
            {children}
          </SidebarContext.Provider>
        </main>
      </div>
    </div>
  );
};

const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <NotificationProvider>
          <div className="App">
            <Toaster
              position="top-right"
              containerStyle={{
                // Clears the header's account menu button in the top-right
                // corner so the toast doesn't sit on top of it.
                top: 80,
              }}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/validate" element={<Validate />} />
              <Route path="/logged-out" element={<LoggedOut />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Documents />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/v4/documents" element={<Navigate to="/v4/documents/list" replace />} />
              <Route path="/v4/documents/list" element={<ProtectedRoute><MainLayout><DocumentsListV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/upload" element={<ProtectedRoute><MainLayout><DocumentsUploadV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/search" element={<ProtectedRoute><MainLayout><DocumentsSearchV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/assistant" element={<ProtectedRoute><MainLayout><DocumentsAssistantV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/settings" element={<ProtectedRoute><MainLayout><DocumentsSettingsV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/import" element={<ProtectedRoute><MainLayout><DocumentsImportV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/import-jobs" element={<ProtectedRoute><MainLayout><DocumentsImportJobsV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/chat-logs" element={<ProtectedRoute><MainLayout><DocumentsChatLogsV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/batches" element={<ProtectedRoute><MainLayout><DocumentsBatchesV4 /></MainLayout></ProtectedRoute>} />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Products />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/inventory"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Inventory />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/import-jobs"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ImportJobs />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/ai-search"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ProductsAiSearchIndexing />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/ai-standardize-jobs"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ProductsAiStandardizeJobs />
                      </Suspense>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/configurable"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ConfigurableProductsList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/configurable/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ConfigurableProducts />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/configurable/edit/:definitionId"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ConfigurableProducts />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/configurable/use/:productCode"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ConfigurableProducts />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/configurable/use/:productCode/cart"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ConfigurableProducts />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenie />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/settings-embed"
                element={
                  <ProtectedRoute>
                    <OrderGenie embedView="settings" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x-v2"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieV2 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x-v3"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieV3 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x-v4"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieV4 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contractors-hub"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ContractorsHub />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/price-sheets"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PriceSheets />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/account-admin"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieAccountAdmin />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/email-inbox"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieEmailInbox />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/mailbox-setup"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieMailboxSetup />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/mcp-server"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <McpServerPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/product-search"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieProductSearch />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieSettings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/usage-dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieUsageDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order-x/workflow-setup"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OrderGenieWorkflowSetup />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              {/* Legacy /order-genie* paths — redirect to the renamed /order-x* routes. */}
              <Route path="/order-genie" element={<LegacyOrderXRedirect to="/order-x" />} />
              <Route path="/order-genie/settings-embed" element={<LegacyOrderXRedirect to="/order-x/settings-embed" />} />
              <Route path="/order-genie-v2" element={<LegacyOrderXRedirect to="/order-x-v2" />} />
              <Route path="/order-genie-v3" element={<LegacyOrderXRedirect to="/order-x-v3" />} />
              <Route path="/order-genie/account-admin" element={<LegacyOrderXRedirect to="/order-x/account-admin" />} />
              <Route path="/order-genie/email-inbox" element={<LegacyOrderXRedirect to="/order-x/email-inbox" />} />
              <Route path="/order-genie/mailbox-setup" element={<LegacyOrderXRedirect to="/order-x/mailbox-setup" />} />
              <Route path="/order-genie/mcp-server" element={<LegacyOrderXRedirect to="/order-x/mcp-server" />} />
              <Route path="/order-genie/product-search" element={<LegacyOrderXRedirect to="/order-x/product-search" />} />
              <Route path="/order-genie/settings" element={<LegacyOrderXRedirect to="/order-x/settings" />} />
              <Route path="/order-genie/usage-dashboard" element={<LegacyOrderXRedirect to="/order-x/usage-dashboard" />} />
              <Route path="/order-genie/workflow-setup" element={<LegacyOrderXRedirect to="/order-x/workflow-setup" />} />
              <Route
                path="/purchase-program/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PurchaseProgram />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/cia/*" element={<CiAdminApp />} />
              <Route
                path="/customer-identity/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerIdentityEmbedded />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/styles"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Styles />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-quality"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DataQuality />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Chat />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configurations"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Configurations />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriData />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/assistant"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriAssistant />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/solr"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSolr />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/opensearch-config"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OpenSearchConfig />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/shop"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemShop />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/shop/v2"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemShopV2 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/shop/v3"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemShopV3 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/system-shop-events"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemShopEvents />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/systems-review"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemsReview />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/featured-management"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemsReview />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/customer-eligibility"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriCustomerEligibility />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/eligibility-rule-sets"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriEligibilityRuleSets />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/shop/compare"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSystemCompare />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/semantix"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SemantixSearch />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/open-search/ecommerce"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <OpenSearchEcommerce />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agentic-shop"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AgenticShop />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-visualizer"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TryoutStudioNewUX showModuleNav={false} />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/ai-tryout-studio" element={<Navigate to="/ai-tryout-studio/try" replace />} />
              <Route path="/ai-image-masking/*" element={<Navigate to="/ai-tryout-studio/try" replace />} />
              <Route
                path="/ai-tryout-studio/new-ux"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TryoutStudioNewUX />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AIImageMaskingSettings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/swatches-v2"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SwatchesV2 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/gallery"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <UserGallery />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/agents"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Agents />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/import"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ZipImport />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/try"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TryBeforeYouBuy />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/try-v2"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TryBeforeYouBuyV2 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coupons"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Coupons />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ReportsDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/define"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ReportsDefine />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ReportsSettings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cad3d"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CAD3D />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/webhook-consumer"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <WebhookConsumer />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/realtime/connections"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RealtimeConnections />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          </NotificationProvider>
        </AuthProvider>
        </Router>
      </QueryClientProvider>
  );
}

export default App;
